import { useEffect, useState } from 'react';
import {
  Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useT } from '../i18n';

// Composes one address part (Street number / Block / Number) from a chain of
// segments. Each segment is Alpha / Number / Custom; segments concatenate
// left-to-right ([Alpha "A"]+[Num "2"] → "A2"). The component owns its segment
// state and reports only the final string via onChange — see
// docs/superpowers/specs/2026-06-27-configurable-unit-address-scheme-design.md.

type SegType = 'alpha' | 'num' | 'custom';

const TYPE_OPTIONS: Array<{ value: SegType; labelKey: string }> = [
  { value: 'alpha', labelKey: 'seg.typeAlpha' },
  { value: 'num', labelKey: 'seg.typeNum' },
  { value: 'custom', labelKey: 'seg.typeCustom' },
];

interface Segment {
  type: SegType;
  value: string;
}

function sanitize(type: SegType, raw: string): string {
  if (type === 'alpha') return raw.replace(/[^A-Za-z]/g, '');
  if (type === 'num') return raw.replace(/[^0-9]/g, '');
  return raw;
}

function compose(segments: Segment[]): string {
  return segments.map((s) => s.value).join('');
}

export function SegmentInput({ label, required, onChange }: {
  label: string;
  required?: boolean;
  onChange: (composed: string) => void;
}) {
  const t = useT();
  const [segments, setSegments] = useState<Segment[]>([{ type: 'num', value: '' }]);

  // Report the composed string up whenever segments change.
  useEffect(() => {
    onChange(compose(segments));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments]);

  function update(idx: number, next: Partial<Segment>) {
    setSegments((prev) =>
      prev.map((s, i) => {
        if (i !== idx) return s;
        const merged = { ...s, ...next };
        // Re-sanitize the value when the type changes or the value is edited.
        return { ...merged, value: sanitize(merged.type, merged.value) };
      }),
    );
  }

  function addSegment() {
    setSegments((prev) => [...prev, { type: 'alpha', value: '' }]);
  }

  function removeSegment(idx: number) {
    setSegments((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  }

  const composed = compose(segments);

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2">{label}{required ? ' *' : ''}</Typography>
        {composed ? (
          <Typography variant="caption" color="text.secondary">
            → <strong>{composed}</strong>
          </Typography>
        ) : null}
      </Stack>
      <Stack spacing={1}>
        {segments.map((seg, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center">
            <TextField
              select size="small" value={seg.type}
              onChange={(e) => update(idx, { type: e.target.value as SegType })}
              sx={{ width: 150, flexShrink: 0 }}
            >
              {TYPE_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{t(o.labelKey)}</MenuItem>))}
            </TextField>
            <TextField
              size="small" fullWidth value={seg.value}
              placeholder={seg.type === 'alpha' ? 'A' : seg.type === 'num' ? '15' : t('seg.customPlaceholder')}
              onChange={(e) => update(idx, { value: e.target.value })}
              inputProps={{ maxLength: 20 }}
            />
            <Tooltip title={t('seg.removeSegment')}>
              <span>
                <IconButton size="small" onClick={() => removeSegment(idx)} disabled={segments.length === 1}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addSegment} sx={{ alignSelf: 'flex-start' }}>
          {t('seg.addSegment')}
        </Button>
      </Stack>
    </Box>
  );
}
